import { useEffect, useState } from 'react';
import { StatTile } from '../../components/StatTile';
import { useRunStore, type CriticalFilesSource } from '../../state/runStore';
import { ManifestPicker } from './ManifestPicker';

interface Props {
  runId: string;
  projectPath: string | null;
  onOpenFile: (relativePath: string) => void;
}

/**
 * Displays the resolved critical files. Mirrors the Phase 3 mockup:
 * stat tiles on top, then Found / Missing / Duplicate tables. Source is
 * picked via a segmented toggle - TEXEC description (auto), JSON/XML/CSV
 * manifest, or `hashes.log`.
 */
export function CriticalFilesCard({ runId, projectPath, onOpenFile }: Props) {
  const {
    criticalFiles,
    criticalFilesLoading,
    criticalFilesError,
    loadCriticalFiles,
    clearCriticalFiles,
  } = useRunStore();
  const [picker, setPicker] = useState<null | {
    title: string;
    extensions: string[];
    source: CriticalFilesSource;
  }>(null);
  const [activeSource, setActiveSource] = useState<CriticalFilesSource>('texec');

  useEffect(() => {
    if (projectPath && !criticalFiles && !criticalFilesLoading) {
      void loadCriticalFiles(runId, 'texec');
    }
  }, [runId, projectPath, criticalFiles, criticalFilesLoading, loadCriticalFiles]);

  const choose = (source: CriticalFilesSource) => {
    setActiveSource(source);
    if (source === 'texec') {
      void loadCriticalFiles(runId, 'texec');
      return;
    }
    if (source === 'manifest') {
      setPicker({
        title: 'Pick a manifest file (.json / .xml / .csv)',
        extensions: ['.json', '.xml', '.csv'],
        source: 'manifest',
      });
      return;
    }
    setPicker({
      title: 'Pick a hashes.log file',
      extensions: ['.log', '.txt'],
      source: 'hashes',
    });
  };

  const onPicked = (path: string) => {
    setPicker(null);
    void loadCriticalFiles(runId, 'manifest', path);
  };

  return (
    <div className="card">
      <div className="row">
        <strong>Critical Files</strong>
        <span className="spacer" />
        <div className="seg-control">
          <button
            className={`seg-btn ${activeSource === 'texec' ? 'active' : ''}`}
            onClick={() => choose('texec')}
            disabled={criticalFilesLoading}
          >
            From TEXEC
          </button>
          <button
            className={`seg-btn ${activeSource === 'manifest' ? 'active' : ''}`}
            onClick={() => choose('manifest')}
            disabled={!projectPath || criticalFilesLoading}
          >
            From Manifest
          </button>
          <button
            className={`seg-btn ${activeSource === 'hashes' ? 'active' : ''}`}
            onClick={() => choose('hashes')}
            disabled={!projectPath || criticalFilesLoading}
          >
            hashes.log
          </button>
          <button
            className="btn btn-ghost small"
            onClick={() => clearCriticalFiles()}
            disabled={!criticalFiles}
            title="Clear loaded critical files"
          >
            Clear
          </button>
        </div>
      </div>

      {!projectPath && (
        <p className="muted small" style={{ marginTop: 8 }}>
          Pull the source code first to enable manifest / hashes lookup.
        </p>
      )}

      {criticalFilesError && (
        <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)', marginTop: 8 }}>
          {criticalFilesError}
        </div>
      )}

      {criticalFiles && (
        <>
          <div className="stat-grid" style={{ marginTop: 12 }}>
            <StatTile label="Files in Manifest" value={criticalFiles.total} />
            <StatTile label="Found" value={criticalFiles.found} tone="good" />
            <StatTile label="Missing" value={criticalFiles.missing} tone={criticalFiles.missing > 0 ? 'bad' : 'default'} />
            <StatTile label="Duplicate Sets" value={criticalFiles.duplicates} tone={criticalFiles.duplicates > 0 ? 'warn' : 'default'} />
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Source: {criticalFiles.source.toUpperCase()}
            {criticalFiles.manifestPath ? ` - ${criticalFiles.manifestPath}` : ''}
          </div>

          <FoundFilesTable
            items={criticalFiles.items.filter((i) => i.status === 'found')}
            onOpen={onOpenFile}
          />
          <MissingFilesTable items={criticalFiles.items.filter((i) => i.status === 'missing')} />
          <DuplicateFilesTable
            items={criticalFiles.items.filter((i) => i.status === 'duplicate')}
            onOpen={onOpenFile}
          />
        </>
      )}

      {!criticalFiles && criticalFilesLoading && (
        <p className="muted" style={{ marginTop: 12 }}>Loading critical files...</p>
      )}
      {!criticalFiles && !criticalFilesLoading && projectPath && (
        <p className="muted small" style={{ marginTop: 12 }}>
          No critical files loaded. Click a source above to load them.
        </p>
      )}

      {picker && (
        <ManifestPicker
          runId={runId}
          acceptedExtensions={picker.extensions}
          title={picker.title}
          onSelect={onPicked}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

interface FoundProps {
  items: Array<{ filename: string; sha1: string | null; version: string | null; resolvedPath: string | null }>;
  onOpen: (path: string) => void;
}

function FoundFilesTable({ items, onOpen }: FoundProps) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 6px' }}>Found Files ({items.length})</h3>
      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Version</th>
              <th>SHA-1</th>
              <th>Resolved Path</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr
                key={`${item.filename}-${i}`}
                className={item.resolvedPath ? 'row-clickable' : ''}
                onClick={() => item.resolvedPath && onOpen(item.resolvedPath)}
              >
                <td>{item.filename}</td>
                <td className="muted small">{item.version ?? '-'}</td>
                <td className="mono small">{item.sha1 ? truncate(item.sha1, 16) : '-'}</td>
                <td className="mono small">{item.resolvedPath ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface MissingProps {
  items: Array<{ filename: string; sha1: string | null }>;
}

function MissingFilesTable({ items }: MissingProps) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 6px', color: 'var(--bad)' }}>Missing Files ({items.length})</h3>
      <div className="data-table-wrap" style={{ borderColor: 'rgba(248, 81, 73, 0.4)' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>File</th>
              <th>SHA-1</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={`${item.filename}-${i}`} className="row-missing">
                <td>{item.filename}</td>
                <td className="mono small">{item.sha1 ? truncate(item.sha1, 16) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface DupProps {
  items: Array<{ filename: string; resolvedPath: string | null; duplicates: string[] }>;
  onOpen: (path: string) => void;
}

function DuplicateFilesTable({ items, onOpen }: DupProps) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 6px', color: 'var(--warn)' }}>Duplicate Filenames ({items.length})</h3>
      <div className="data-table-wrap" style={{ borderColor: 'rgba(210, 153, 34, 0.4)' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Matches</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={`${item.filename}-${i}`}>
                <td>{item.filename}</td>
                <td>
                  {item.duplicates.map((dup) => (
                    <div
                      key={dup}
                      className="dup-row"
                      onClick={() => onOpen(dup)}
                      title="Open file"
                    >
                      <span className="mono small">{dup}</span>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n / 2)}...${s.slice(-n / 2)}`;
}
